import { Config } from "src/modules/app-template/models/config.model";
import { MainAppService } from "src/modules/app-template/services/main-app.service";
import { EnaioUtils } from "src/modules/enaio/misc/enaio-utils";
import { EnaioCall } from "src/modules/enaio/shared/EnaioCall";
import { EnaioClientEvent } from "src/modules/enaio/shared/EnaioClientEvent";
import { EnaioScriptType } from "src/modules/enaio/shared/EnaioScriptType";
import { EnaioWorkflowEventType } from "src/modules/enaio/shared/EnaioWorkflowEventType";
import { EnaioWorkflowModel } from "src/modules/enaio/shared/EnaioWorkflowModel";
import { FrontendFormDefinition } from "src/modules/sm-base/models/frontend-form-definition.model";
import { HttpResult } from "src/modules/sm-base/models/http-connector.model";
import { RestEndpoint } from "src/modules/sm-base/models/rest-endpoint.model";
import { SmTimer } from "src/modules/sm-base/models/sm-timer.model";
import { Progressor } from "src/modules/utils/misc/Progressor";
import { ProgressorResult } from "src/modules/utils/misc/Progressor-Result";
import { Wrapper } from "src/modules/utils/misc/wrapper.model";
import { OrdinaryObject, OrdinaryObjectNumber } from "src/modules/utils/shared/ordinary-object.model";
import { Utils } from "src/modules/utils/shared/utils";
import { FrontendScriptDefinition } from "./frontend-script-definition.model";
import { FrontendScriptNode } from "./frontend-script-node.model";
import { FrontendScriptsDefinition } from "./frontend-scripts-definition.model";
import { FrontendScriptPastVersion } from "./frontend-script-past-version.model";
import { TreeIcon } from "src/modules/sm-base/models/tree-icon.model";
import { FrontendScriptOutlineItem } from "./frontend-script-outline-item.model";

export class DhTools {

    static async enaioCall<T>(options: EnaioCall<T>): Promise<T> {
        let result = await this.backendCall("api/enaio/call", {className: "CsUtils." + options.constructor.name, options}).getText();
        return options.getModelType().fromPlainUnsafe(Utils.fromJson(result)) as T;
    }

    static backendCall(url: string, body: OrdinaryObject = null): HttpResult {
        return this.rest().get().post().body(body).run(url);
    }

    static rest(): RestEndpoint {
        return new RestEndpoint(Config.get().backendUrl);
    }

    static async prettifyJson(json: string): Promise<string> {
        return this.backendCall("api/tools/prettifyJson", {json}).getText();
    }

    static async prettifyXml(xml: string): Promise<string> {
        return this.backendCall("api/tools/prettifyXml", {xml}).getText();
    }

    static async decodeUtf16(s: string): Promise<string> {
        return this.backendCall("api/tools/decodeUtf16", {s}).getText();
    }

    static async openDialog(fileName?: string, selectFolder?: boolean): Promise<string> {
        return this.backendCall("api/system/openSaveDialog", Utils.objectRemoveNullValues({ openDialog: true, fileName, selectFolder })).getString();
    }

    static async saveDialog(fileName?: string, selectFolder?: boolean): Promise<string> {
        return this.backendCall("api/system/openSaveDialog", Utils.objectRemoveNullValues({ fileName, selectFolder })).getString();
    }

    static showDateIntervalOptions(app: MainAppService, form: FrontendFormDefinition, beginDateFieldName = "beginDate", endDateFieldName = "endDate"): void {
        let now = new Date();
        let model = [
            {
                label: "Heute",
                command: () => {
                    form.setValue(beginDateFieldName, Utils.dateStartOf(now, "day"));
                    form.setValue(endDateFieldName, Utils.dateEndOf(now, "day"));
                }
            },
            {
                label: "Gestern",
                command: () => {
                    form.setValue(beginDateFieldName, Utils.dateAdd(Utils.dateStartOf(now, "day"), "day", -1));
                    form.setValue(endDateFieldName, Utils.dateAdd(Utils.dateEndOf(now, "day"), "day", -1));
                }
            },
            {
                label: "Seit Gestern",
                command: () => {
                    form.setValue(beginDateFieldName, Utils.dateAdd(Utils.dateStartOf(now, "day"), "day", -1));
                    form.setValue(endDateFieldName, Utils.dateEndOf(now, "day"));
                }
            },
            {
                label: "Diese Woche",
                command: () => {
                    form.setValue(beginDateFieldName, Utils.dateStartOf(now, "week"));
                    form.setValue(endDateFieldName, Utils.dateEndOf(now, "day"));
                }
            },
            {
                label: "Eine Woche",
                command: () => {
                    form.setValue(beginDateFieldName, Utils.dateAdd(Utils.dateStartOf(now, "day"), "week", -1));
                    form.setValue(endDateFieldName, Utils.dateEndOf(now, "day"));
                }
            },
            {
                label: "Dieser Monat",
                command: () => {
                    form.setValue(beginDateFieldName, Utils.dateStartOf(now, "month"));
                    form.setValue(endDateFieldName, Utils.dateEndOf(now, "day"));
                }
            },
            {
                label: "Ein Monat",
                command: () => {
                    form.setValue(beginDateFieldName, Utils.dateAdd(Utils.dateStartOf(now, "day"), "month", -1));
                    form.setValue(endDateFieldName, Utils.dateEndOf(now, "day"));
                }
            },
            {
                label: "Dieses Jahr",
                command: () => {
                    form.setValue(beginDateFieldName, Utils.dateStartOf(now, "year"));
                    form.setValue(endDateFieldName, Utils.dateEndOf(now, "day"));
                }
            },
            {
                label: "Ein Jahr",
                command: () => {
                    form.setValue(beginDateFieldName, Utils.dateAdd(Utils.dateStartOf(now, "day"), "year", -1));
                    form.setValue(endDateFieldName, Utils.dateEndOf(now, "day"));
                }
            }
        ];
        app.openContextMenu(model);
    }

    static startProgressor(progressor: Wrapper<Progressor>, guid: string, onFinished?: (result: ProgressorResult) => void, onError?: (value: string) => void): void {
        let timer = new SmTimer(0, 100, async () => {
            progressor.value = (await DhTools.backendCall("api/dh/getProgressors").list(Progressor)).find(p => p.guid == guid);

            if (progressor.value == null) {
                timer.destroy();
                let result = await DhTools.backendCall("api/dh/getProgressorResult", { guid }).get(ProgressorResult);
                if (result.error && onError != null) {
                    onError(result.value);
                }
                else {
                    onFinished?.(result);
                }
            }
        }, true);
    }

    static workflowToScriptDefinition(model: EnaioWorkflowModel, pastModels: EnaioWorkflowModel[]): FrontendScriptsDefinition {
        let result = new FrontendScriptsDefinition();
        result.nodes.push(new FrontendScriptNode("0_activities", "Aktivitäten", "0"));
        result.nodes.push(new FrontendScriptNode("0_globals", "Globale Skripte", "1"));
        
        let globalClientEvents: OrdinaryObjectNumber = {};
        let globalServerEvents: OrdinaryObjectNumber = {};
        for (let event of model.definition.events) {
            if (event.scriptType == EnaioScriptType.clientGlobal) {
                globalClientEvents[event.language] = event;
            }
            else if (event.scriptType == EnaioScriptType.serverGlobal) {
                globalServerEvents[event.language] = event;
            }
        }
        for (let event of model.definition.events) {
            if (EnaioUtils.isClientTypeRelevant(event.clientTypeId)) {
                let isServer = event.scriptType == EnaioScriptType.server || event.scriptType == EnaioScriptType.serverGlobal;
                let isJs = event.language == 2;
                let name = event.getSpeakingName();
                let activity = model.definition.activities.find(a => a.id == event.appId);
                let sort = 
                    event.type == EnaioWorkflowEventType.startActivity ? "0" : 
                    event.type == EnaioWorkflowEventType.beforeOpen ? "1" : 
                    event.type == EnaioWorkflowEventType.beforeCancel || event.type == EnaioWorkflowEventType.beforeForward || event.type == EnaioWorkflowEventType.beforeForwardTo ? "7" : 
                    event.type == EnaioWorkflowEventType.afterForward ? "8" : 
                    event.type == EnaioWorkflowEventType.endActivity ? "9" : "5";
                result.scripts.push(Utils.fromPlain(FrontendScriptDefinition, {
                    id: event.id,
                    name,
                    sortName: sort + name,
                    icons: Utils.arrayWithoutNull([isServer ? new TreeIcon("isServer", "fas fa-server", "text-blue-500") : null, isJs ? new TreeIcon("isJs", "fa-brands fa-chrome", "text-green-500") : null]),
                    language: isJs ? "javascript" : "vbscript",
                    parents: event.scriptType == EnaioScriptType.clientGlobal || event.scriptType == EnaioScriptType.serverGlobal ? ["0_globals"] :
                        ["0_activities", result.getOrAddNode("act_" + activity?.id, () => activity?.name, () => (activity?.name == "StartActivity" ? "0" : activity?.name == "EndActivity" ? "2" : "1") + activity?.name).id],
                    code: event.code,
                    includes: event.type < 10_000 && Utils.hasProperty(globalClientEvents, event.language) ? [ globalClientEvents[event.language].id ] :
                        event.type >= 10_000 && event.type < 1_000_000 && Utils.hasProperty(globalServerEvents, event.language) ? [ globalServerEvents[event.language].id ] : [],
                    raw: event,
                    version: Utils.toString(model.version)
                }));
            }
        }

        for (let param of model.definition.dataFields) {
            result.globalOutlineItems.push(new FrontendScriptOutlineItem(param.id, "workflowVariable", param.name, { 
                parents: ["Workflow-Parameter"], 
                searchRegex: "(?<![a-zA-Z])(?<result>" + param.name + ")(?![a-zA-Z])"}))
        }

        if (pastModels != null) {
            for (let model2 of pastModels) {
                if (model2.definition?.events != null) {
                    for (let event of model2.definition.events) {
                        let current = result.scripts.find(s => s.id == event.id);
                        if (current != null) {
                            current.pastVersions ??= [];
                            current.pastVersions.push(new FrontendScriptPastVersion(model2.name + " (v" + model2.version + ")", event.code, Utils.expandNumber(model2.version, 10)));
                        }
                    }
                }
            }
        }

        result.calculate();
        return result;
    }

    static objectTypeToScriptDefinition(events: EnaioClientEvent[]): FrontendScriptsDefinition {
        let result = new FrontendScriptsDefinition();
        result.nodes.push(new FrontendScriptNode("0_fromObjectType", "Objekttyp", "0"));
        result.nodes.push(new FrontendScriptNode("0_globals", "Globale Skripte", "1"));
        
        let globalClientEvent = events.find(e => e.eventCode.name == "GlobalClientScript");
        let globalServerEvent = events.find(e => e.eventCode.name == "GlobalServerScript");
        for (let event of events) {
           // if (EnaioUtils.isClientTypeRelevant(event.clientTypeId)) {
                let name = event.getCaption(true) /*+
                    (event.clientTypeId == EnaioUtils.CLIENT_ID_ENAIO ? "" : " (" + EnaioUtils.getClientTypeName(event.clientTypeId) + ")")*/;
                result.scripts.push(Utils.fromPlain(FrontendScriptDefinition, {
                    id: event.calculatedSpeakingId,
                    name,
                    language: "vbscript",
                    parents: event.eventCode.name == "GlobalClientScript" || event.eventCode.name == "GlobalServerScript" ? ["0_globals"] : ["0_fromObjectType"],
                    code: event.code,
                    includes: event.eventCode.appClass == "Client" && event.eventCode.name != "GlobalClientScript" && globalClientEvent != null ? [ globalClientEvent.calculatedSpeakingId ] :
                    event.eventCode.appClass == "Server" && event.eventCode.name != "GlobalServerScript" && globalServerEvent != null ? [ globalServerEvent.calculatedSpeakingId ] : [],
                    raw: event
                }));
           // }
        }

        result.calculate();
        return result;
    }

}
